import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatElapsed, useElapsed } from '../lib/elapsed';
import { onTimerChange, timerChanged } from '../lib/timer';
import type { RunningTimer as Running } from '../lib/types';

/**
 * The running timer, on every page.
 *
 * A timer can now be started from a task, which is what it should be — but it left a gap: you
 * could start one, close the drawer, and have nothing anywhere tell you it was still going.
 * The only way to find out was to open the Timesheet, which is the page the task button exists
 * to avoid. A clock nobody can see is how somebody books eleven hours to one task by accident.
 *
 * So it lives in the shell beside the notification bell, and it is the SAME timer as the one on
 * the task and the one on the timesheet — there is only ever one per person, and all three read
 * `/time/timer`. Stopping it here stops it everywhere, because there is nothing else to stop.
 *
 * WHY IT POLLS. The three places can each start and stop, so any of them can be out of date the
 * moment another acts. Sixty seconds is enough: the elapsed figure ticks locally from
 * `startedAt` and needs no help, so the poll is only there to notice a timer that started or
 * stopped somewhere else — a minute late for that is nobody's problem.
 */
export function RunningTimerChip() {
  const [timer, setTimer] = useState<Running | null>(null);
  const [busy, setBusy] = useState(false);
  const seconds = useElapsed(timer?.startedAt);

  const load = useCallback(async () => {
    try {
      setTimer(await api.get<Running | null>('/time/timer'));
    } catch {
      /* Not signed in, or no permission to log time. Either way: nothing to show. */
      setTimer(null);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 60_000);
    /* And the moment a tab comes back, rather than up to a minute after. */
    const onVisible = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onVisible);
    /* And immediately when this tab starts or stops it — the poll is too slow to be sync. */
    const unsubscribe = onTimerChange(() => void load());
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      unsubscribe();
    };
  }, [load]);

  if (!timer) return null;

  const where = timer.workItem ? timer.workItem.key : timer.projectName;
  const title = timer.workItem ? `${timer.workItem.key} — ${timer.workItem.title}` : timer.projectName;

  return (
    <div className="running-timer" title={title}>
      <span className="dot-pulse" aria-hidden />
      <Link to="/timesheets" className="running-timer-where truncate">
        {where}
      </Link>
      {/*
        `aria-live` so a screen reader is told the timer is going without being read the
        changing number every second, which would be unusable. The clock itself is marked
        `aria-hidden` and the label carries the meaning.
      */}
      <span className="mono running-timer-clock" aria-hidden>
        {formatElapsed(seconds)}
      </span>
      <span className="sr-only" aria-live="polite">
        Timer running on {where}
      </span>
      <button
        type="button"
        className="running-timer-stop"
        disabled={busy}
        aria-label={`Stop the timer on ${where}`}
        title="Stop the timer"
        onClick={async () => {
          setBusy(true);
          try {
            await api.post('/time/timer/stop');
            await load();
            timerChanged();
          } finally {
            setBusy(false);
          }
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
        </svg>
      </button>
    </div>
  );
}
